import {objectFromEntries} from '@benchling/js-utils';

import {
  DataFrame,
  DataFrameColumnDef,
  DataFrameColumnType,
  DataType,
  DeprecatedDataFrame,
} from 'data-frames/DataFrameTypes';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {tryDeserializeDecimalToFloat} from 'data-types/util/Deserializers';
import {JSONValue} from 'util/Types';

import resolveDataFrame from '../resolveDataFrame';
import {DataFrameValueResolutionMode} from '../resolveDataFrameValue';

/** Most leaf types serialize as strings; for maintainability only define the exceptions */
const LEAF_TYPE_TO_NON_STRING_LEGACY_DATA_TYPE: {[key in LeafType]?: DataType} = {
  [LeafType.JSON]: DataType.JSON,
  [LeafType.BLOB]: DataType.JSON,
  [LeafType.BOOLEAN]: DataType.BOOLEAN,
  [LeafType.DECIMAL]: DataType.NUMBER,
  [LeafType.INTEGER]: DataType.NUMBER,
  [LeafType.DATE]: DataType.DATE_OR_TIMESTAMP,
  [LeafType.DATETIME]: DataType.DATE_OR_TIMESTAMP,
};

/**
 * Convert a data frame column type to a legacy data type.
 */
export function columnTypeToLegacyDataType(columnType: DataFrameColumnType): DataType {
  if (columnType.isList) {
    return DataType.JSON;
  }
  if (LEAF_TYPE_TO_NON_STRING_LEGACY_DATA_TYPE.hasOwnProperty(columnType.leafType)) {
    return columnType.maybeText
      ? DataType.JSON
      : LEAF_TYPE_TO_NON_STRING_LEGACY_DATA_TYPE[columnType.leafType]!;
  }
  return DataType.STRING;
}

function convertValue(value: JSONValue, columnType: DataFrameColumnType): JSONValue {
  return columnType.leafType === LeafType.DECIMAL ? tryDeserializeDecimalToFloat(value).value : value;
}

function convertRow(
  columns: Array<DataFrameColumnDef>,
  dataFrameRow: Record<string, JSONValue>
): {[key: string]: JSONValue} {
  return objectFromEntries(
    columns.map(({systemName, type}) => [systemName, convertValue(dataFrameRow[systemName], type)])
  );
}

/**
 * Convert a DataFrame to DeprecatedDataFrame with the same column names.
 *
 * As part of the conversion:
 * - Each DataFrameColumnType will be translated to a legacy DataType.
 * - Decimal values will be deserialized from string to number.
 */
export default function convertDataFrameToDeprecatedDataFrame<TPointType extends {[key: string]: JSONValue}>(
  dataFrame: DataFrame
): DeprecatedDataFrame<TPointType> {
  const resolvedDataFrame = resolveDataFrame(dataFrame, {
    apiIdentifiedObjects: DataFrameValueResolutionMode.NONE,
    blobs: DataFrameValueResolutionMode.WAREHOUSE_BLOB_DICT,
  });

  const axisTypes = objectFromEntries(
    resolvedDataFrame.columns.map(({systemName, type}) => [systemName, columnTypeToLegacyDataType(type)])
  );

  return {
    axisTypes,
    points: resolvedDataFrame.rows.map((row) => convertRow(resolvedDataFrame.columns, row)),
  } as DeprecatedDataFrame<TPointType>;
}
